import { ISPListSchema, ISPListField } from '../spListItemRetriever/interfaces/ISPGetItemResponse';
import { ISPListColumn, ISPListItem } from './ISPListItemData';
import { ColumnFieldType, ColumnWidth, MappedColumnType, ShowInFiltersPaneStatus } from './SPListItemEnums';
import { ISPListContext, IGroupSchemaMap } from '../spListItemRetriever/interfaces/ISPListContext';
import { ISPGetItemContext } from '../spListItemRetriever/interfaces/ISPGetItemContext';
import ListFilterUtilities from '../../../utilities/list/ListFilterUtilities';
import * as CamlParsing from '../../../utilities/caml/CamlParsingBasic';
import { getRelativeDateTimeStringForLists } from '@ms/odsp-utilities/lib/dateTime/DateTime';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';
import ListTemplateType from '../../listCollection/ListTemplateType';
import { getColumnFieldType } from './SchemaBuilderBasic';

interface IMappedColumnDefinition {
    index: number; // column index in the list view
    key: string;
    isVisibleMobile: boolean;
    isCollapsable: boolean;
    isRowHeader?: boolean;
    minWidth?: number;
    width?: number;
    type: ColumnFieldType;
    sortDescFirst?: boolean;
}

// The following field categories from listSchema always map to the given column definitions.
// (See ColumnsHelper._getMappedColumnType for how field names map to field categories.)
// The exception is that if multiple fields map to the same column index (this will happen if
// a view includes both a name field and a title field), the first one will use the mapped
// definition/index and subsequent ones will be ordered with the rest of the fields.
const SCHEMA_MAP: { [key: number]: IMappedColumnDefinition } = {
    [MappedColumnType.icon]: { key: 'type', index: 0, isVisibleMobile: true, isCollapsable: false, type: ColumnFieldType.FileIcon }, // width will be set below
    [MappedColumnType.name]: { key: 'name', index: 1, isRowHeader: true, isVisibleMobile: true, isCollapsable: false, minWidth: ColumnWidth.nameMin, width: ColumnWidth.nameODB, type: ColumnFieldType.Name },
    [MappedColumnType.title]: { key: 'Title', index: 1, isVisibleMobile: true, isCollapsable: false, minWidth: ColumnWidth.nameMin, width: ColumnWidth.nameODB, type: ColumnFieldType.Title },
    [MappedColumnType.shareHeroCommand]: { key: '_shareHeroCommand', index: 2, isVisibleMobile: false, isCollapsable: true, minWidth: 16, width: 18, type: ColumnFieldType.ShareCommand },
    [MappedColumnType.calloutInvoker]: { key: '_calloutInvoker', index: 3, isVisibleMobile: false, isCollapsable: true, minWidth: 16, width: 18, type: ColumnFieldType.DotDotDot },
    [MappedColumnType.modified]: { key: 'dateModified', index: 4, isVisibleMobile: true, isCollapsable: false, minWidth: ColumnWidth.regularMin, width: ColumnWidth.regular, type: ColumnFieldType.DateTime, sortDescFirst: true },
    [MappedColumnType.url]: { key: 'URL', index: 5, isVisibleMobile: true, isCollapsable: false, minWidth: ColumnWidth.nameMin, width: ColumnWidth.nameODB, type: ColumnFieldType.Hyperlink }
};
// number of columns we will usually fill up from the schemaMap; must be kept in sync with schemaMap
// (value = max index in schemaMap + 1)
const numSchemaMappedColumns = 5;

// fields to exclude from any returned list schema
export const EXCLUDED_FIELDS = [
    '_ip_UnifiedCompliancePolicyUIAction',
    '_CommentFlags',
    'Edit', // icon with link to edit item (deprecated)
    'MediaServiceFastMetadata',
    'MediaServiceOCR',
    'MediaServiceTranscript',
    'name.FileSystemItemId',
    'SMTotalFileCount',
    'SMTotalSize'
];

// additional fields to exclude from returned list schema for fixed schema/OneDrive scenarios
const EXCLUDED_FIELDS_FIXED = EXCLUDED_FIELDS.concat([
    'FolderChildCount',
    'ItemChildCount'
]);

// For now only single line text type columns can be autoResized, since for columns such as note that
// might render multiple lines, we cannot easily determine the longest line based on the field value
const CAN_AUTO_RESIZE_TYPES = [
    ColumnFieldType.Text,
    ColumnFieldType.Name,
    ColumnFieldType.Title,
    ColumnFieldType.DateTime,
    ColumnFieldType.Number
];

export namespace SchemaBuilder {
    export interface ISchemaBuilderOptions {
        disableCalloutInvoker?: boolean;
        calloutInvokerDisplayName?: string;
        hasFixedSchema?: boolean;
        serverSupportsAutoHyperLink?: boolean;
        useCustomColumn?: boolean;
        shareHeroCommandDisplayName?: string;
        disableShareHeroCommand?: boolean;
    }

    export function buildSchema(listSchema: ISPListSchema,
        listContext: ISPListContext,
        options: ISchemaBuilderOptions,
        groupSchemaMap: IGroupSchemaMap,
        getItemContext?: ISPGetItemContext): ISPListColumn[] {
        if (!listSchema || !listSchema.Field) {
            return undefined;
        }

        // If any of the fields in the schema indicate that a ... (callout invoker) should be present,
        // add a definition for it unless it has been explicitly disabled.
        let titleFieldIndex = -1;
        let count = 0;
        for (let listField of listSchema.Field) {
            if (listField.listItemMenu === 'TRUE') {
                titleFieldIndex = count;
                break;
            }
            count++;
        }

        if (!options.disableCalloutInvoker && titleFieldIndex >= 0) {
            listSchema.Field.splice(titleFieldIndex + 1, 0, {
                DisplayName: options.calloutInvokerDisplayName,
                Name: '_calloutInvoker',
                Sortable: 'FALSE',
                Type: 'Computed'
            });
        }

        if (!options.disableShareHeroCommand && titleFieldIndex >= 0) {
            listSchema.Field.splice(titleFieldIndex + 1, 0, {
                DisplayName: options.shareHeroCommandDisplayName,
                Name: "_shareHeroCommand",
                Sortable: 'FALSE',
                Type: 'Computed'
            });
        }

        // Initialize an array of columns with empty spaces for the potential schema mapped columns
        let schema = new Array(options.hasFixedSchema ? numSchemaMappedColumns : 0);

        let iconColumn;
        let nameColumn;
        let modifiedColumn;
        let listColumnMap: { [field: string]: ISPListColumn } = {};
        for (let listField of listSchema.Field) {
            let fieldName = listField.Name;
            let excludedFields = options.hasFixedSchema ? EXCLUDED_FIELDS_FIXED : EXCLUDED_FIELDS;

            if (excludedFields.indexOf(fieldName) !== -1) {
                continue; // skip field if it's one of the excluded fields
            }

            // Get the field mapping if applicable
            let mappedType = getMappedColumnType(fieldName);
            let mappedDef = SCHEMA_MAP[mappedType];
            // Get the column definition based on a combination of the base field definition and mapping (if present)
            let columnDef = _getFieldFromListData(listField, mappedDef, listContext, options, listSchema, getItemContext);

            if (listField.GroupField === 'TRUE' && groupSchemaMap) {
                // Any fields used for grouping, if the field exist in the view,
                // it will occur twice in the list schema (only the second occurrence will have
                // GroupField='TRUE'). Add the field to the group schema but not the list of fields the second time.
                groupSchemaMap[fieldName] = listField as any;
                if (listColumnMap[fieldName]) {
                    continue;
                } else {
                    // When the field for the grouping doesn't exist in the view,
                    // we still want to include it in the schema but need to make sure it is invisible.
                    columnDef.isVisible = false;
                    columnDef.isVisibleMobile = false;
                }
            }

            // Mapped fields go at the index specified in the mapping (unless another field is already in that spot).
            // Other fields are appended to the end of the schema.
            let columnIdx = options.hasFixedSchema && mappedDef && !schema[mappedDef.index] ? mappedDef.index : schema.length;
            schema[columnIdx] = columnDef;
            listColumnMap[fieldName] = columnDef;

            switch (mappedType) {
                case MappedColumnType.icon:
                    iconColumn = columnDef;
                    break;
                case MappedColumnType.name:
                    nameColumn = columnDef;
                    break;
                case MappedColumnType.modified:
                    modifiedColumn = columnDef;
                    break;
            }
        }

        // The group by view normally uses the file type icon field to display the expand/collapse group icon.
        // If we're in a grouped view but this is not a doclib (or if the filename column has been removed
        // for some reason), there won't be a file type icon field.
        // Add a placeholder field instead.
        let isGroupedView = listContext.groupBy || listSchema.group1 || listSchema.group2;
        if (isGroupedView && (!iconColumn || iconColumn !== schema[0])) {
            let placeholderField = _getDefaultColumn('other', '');
            placeholderField.minWidth = _getIconColumnMinWidth(options.useCustomColumn);
            placeholderField.width = _getIconColumnWidth(options.useCustomColumn);
            placeholderField.fieldType = ColumnFieldType.Computed;
            schema.unshift(placeholderField);
        }

        if (options.useCustomColumn) {
            return getCustomColumns(iconColumn, [[nameColumn], [modifiedColumn]], [nameColumn, modifiedColumn]);
        } else {
            // Remove empty entries and return the resulting schema.
            return schema.filter((def: ISPListColumn) => !!def);
        }
    }

    export function getMappedColumnType(fieldName: string): MappedColumnType {
        switch (fieldName) {
            case 'FSObjType':
            case 'DocIcon':
                return MappedColumnType.icon;
            case 'LinkFilename':
            case 'LinkFilenameNoMenu':
            case 'FileLeafRef':
            case 'LinkIssueIDNoMenu':
                return MappedColumnType.name;
            case 'LinkTitle':
            case 'LinkTitleNoMenu':
            case 'Title':
                return MappedColumnType.title;
            case '_calloutInvoker':
                return MappedColumnType.calloutInvoker;
            case '_shareHeroCommand':
                return MappedColumnType.shareHeroCommand;
            case 'Modified':
                return MappedColumnType.modified;
            case "URLwMenu":
            case "URLNoMenu":
                return MappedColumnType.url;
            default:
                return MappedColumnType.none;
        }
    }

    export function getCustomColumns(itemTypeColumn: ISPListColumn, columnRows: ISPListColumn[][], sorts: ISPListColumn[]): ISPListColumn[] {
        'use strict';

        let customColumn = _getCustomColumn();
        for (let row = 0; row < columnRows.length; row++) {
            let fields = columnRows[row];
            for (let field of fields) {
                field.customRowNumber = row;
                customColumn.customFields.push(field);
            }
        }
        let columns = [itemTypeColumn, customColumn];

        // Let's add fields that we should be able to sort by to the schema, but make sure they are not
        // visible since the customColumn is already showing its data
        for (let sort of sorts) {
            sort.isVisible = false;
            sort.isVisibleMobile = false;
            columns.push(sort);
        }

        return columns;
    }

    export function getMappedColumnFieldType(field: ISPListField): ColumnFieldType {
        let mappedType = getMappedColumnType(field.Name);
        let mappedDef = SCHEMA_MAP[mappedType];
        return mappedDef ? mappedDef.type : getColumnFieldType(field);
    }

    function _getFieldFromListData(
        listField: ISPListField,
        mappedDef: IMappedColumnDefinition,
        listContext: ISPListContext,
        options: ISchemaBuilderOptions,
        listSchema: ISPListSchema,
        getItemContext: ISPGetItemContext
    ): ISPListColumn {
        let fieldName = listField.Name;
        let fieldType = mappedDef ? mappedDef.type : getColumnFieldType(listField);
        let isIconField = mappedDef ? mappedDef.key === 'type' : false;

        let canAutoResize = CAN_AUTO_RESIZE_TYPES.indexOf(fieldType) > -1;

        // IsFiltered: from filterParams
        let filterParams = listContext.filterParams || '';
        let isFiltered = !!ListFilterUtilities.getFilterFieldByName(filterParams, fieldName);

        // IsFiltered: from additionalFiltersXml
        if (!isFiltered && getItemContext && getItemContext.additionalFiltersXml) {
            isFiltered = !!(getItemContext.additionalFiltersXml.match(new RegExp('FieldRef Name="' + fieldName + '"')));
        }

        // IsFiltered: from viewXmlForRequest
        // When listContext has viewXmlForRequest, we need to check whether the fieldname is in the view filters.
        // This will make sure when use smart filter, the corresponding column header can show filter icon.
        // And only filters added by smart filter will have id attribute.
        if (listContext.viewXmlForRequest) {
            try {
                let viewDom = CamlParsing.getViewDomParts(listContext.viewXmlForRequest);
                if (viewDom && viewDom.where) {
                    let filterElements = viewDom.where.querySelectorAll(`[id="${fieldName}"]`);
                    if (filterElements.length === 1) {
                        isFiltered = true;
                    }
                }
            } catch (e) { }
        }

        let columnDef: ISPListColumn = {
            key: mappedDef ? mappedDef.key : fieldName,
            name: listField.DisplayName,
            internalName: fieldName,
            staticName: listField.StaticName,
            fieldType: fieldType,
            width: ColumnWidth.regular, // width and minWidth could be reset later in some cases
            minWidth: ColumnWidth.regularMin,
            isVisible: true,
            isVisibleMobile: mappedDef ? mappedDef.isVisibleMobile : true,
            isCollapsable: mappedDef ? mappedDef.isCollapsable : true,
            canAutoResize: canAutoResize,
            isIcon: isIconField,
            resizable: true,
            isRowHeader: mappedDef ? mappedDef.isRowHeader : false,
            sortable: listField.Sortable !== 'FALSE',
            filterable: !(listField.Filterable === 'FALSE' || listField.FilterDisable === 'TRUE' ||
                listField.FieldType === 'Note' || listField.FieldType === 'URL' ||
                fieldName === 'FileSizeDisplay' || fieldName === '_IsRecord'),
            groupable: (listField.Groupable !== 'FALSE' && listField.Sortable !== 'FALSE'
                && _isGroupableColumn(fieldName, fieldType)),
            editable: !(listField.FromBaseType && listField.FromBaseType === 'TRUE'),
            isGrouped: listContext.groupByOverride === fieldName || listSchema.group1 === fieldName || listSchema.group2 === fieldName,
            isSorted: listContext.sortField === fieldName,
            isAscending: listContext.sortField === fieldName ? listContext.isAscending === 'true' : true,
            sortDescFirst: mappedDef ? !!mappedDef.sortDescFirst : false,
            isFiltered: isFiltered,
            filterValues: ListFilterUtilities.getFilterValueByName(filterParams, fieldName),
            isRequired: !!listField.Required,
            isAutoHyperLink: mappedDef ? options.serverSupportsAutoHyperLink : (!!listField.AutoHyperLink && options.serverSupportsAutoHyperLink) ||
                (fieldType === ColumnFieldType.Note && listField.RichText !== 'FALSE'),
            isAppendOnly: listField.AppendOnly && listField.AppendOnly === 'TRUE',
            choices: listField.Choices,
            dispFormUrl: listField.DispFormUrl,
            pinnedToFiltersPane: listField.PinnedToFiltersPane && listField.PinnedToFiltersPane === 'TRUE',
            showInFiltersPane: listField.ShowInFiltersPane && ShowInFiltersPaneStatus[listField.ShowInFiltersPane],
            clientSideComponentId: listField.ClientSideComponentId,
            clientSideComponentProperties: listField.ClientSideComponentProperties,
            customFormatter: listField.CustomFormatter,
            serverFieldType: listField.Type,
            resultType: listField.ResultType,
            isCalculated: listField.role === "Calculated",
            isNote: fieldType === ColumnFieldType.Note && listField.RichText === 'FALSE',
            clientSideColumnAdapter: listField.ClientSideColumnAdapter,
            sspId: listField.SspId,
            termSetId: listField.TermSetId,
            anchorId: listField.AnchorId,
            realFieldName: listField.RealFieldName,
            hasListItemMenu: listField.listItemMenu === "TRUE",
            linkToItem: !!listField.LinkToItem,
            isLink: listField.Name === "URLwMenu" && listContext.listTemplateType === ListTemplateType.links
        };
        if (listField.ID) {
            // in SharePoint, the ID field is a guid, and is used by the REST API instead of internal name
            // for schema operations like rename a field or modify a field's definition
            columnDef.id = listField.ID;
        }
        // Reset width and minWidth for some field types
        if (mappedDef) {
            columnDef.minWidth = isIconField ? _getIconColumnMinWidth(options.useCustomColumn) : mappedDef.minWidth;
            columnDef.width = isIconField ? _getIconColumnWidth(options.useCustomColumn) : mappedDef.width;
        }
        if (fieldType === ColumnFieldType.SharedWith) {
            columnDef.width = ColumnWidth.sharedWith; // for sharing hints and other computed fields.
        } else if (fieldType === ColumnFieldType.Note) {
            columnDef.width = ColumnWidth.note;
        } else if (fieldType === ColumnFieldType.AverageRating) {
            columnDef.minWidth = ColumnWidth.ratingsMin;
        } else if (fieldType === ColumnFieldType.DateTime &&
            !Killswitch.isActivated("5BF871F9-43C4-415C-8FEF-5288A169E0F8", "11/13/2017", "Support friendly date time columns")) {
            // SP DateTime fields need overriden behavior to handle friendly date values
            columnDef.getValue = (item: ISPListItem) => _getDateColumnValue(columnDef, item);
        }

        return columnDef;
    }

    function _getColumnKeyValue(columnKey: string, item: ISPListItem): string {
        let value = '';

        // ported from odsp-next's BaseText.getCellText()
        if (item) {
            value = item[columnKey];

            if (value === undefined && item.properties) {
                value = item.properties[columnKey];

                if (value === undefined && item.recycleBinProperties) {
                    value = item.recycleBinProperties[columnKey];
                }

                if (value && Array.isArray(value)) {
                    value = (value as any).join(', ');
                }
            }
        }

        return value;
    }

    function _getDateColumnValue(column: ISPListColumn, item: ISPListItem): string {
        const friendlyDateSuffix = '.FriendlyDisplay';
        let value = undefined;

        if (column && item) {
            // try to use the server-rendered friendly date, otherwise fallback to default column
            value = _getColumnKeyValue(column.key + friendlyDateSuffix, item);

            if (value) {
                // got a friendly date, apply client-side formatting to it
                value = getRelativeDateTimeStringForLists(value);
            } else {
                value = _getColumnKeyValue(column.key, item);
            }
        }

        return value;
    }

    function _getIconColumnWidth(useCustomColumn: boolean): number {
        const iconColumnWidth = useCustomColumn ? ColumnWidth.bigIcon : ColumnWidth.icon;
        return iconColumnWidth;
    }

    function _getIconColumnMinWidth(useCustomColumn: boolean): number {
        const iconColumnWidth = useCustomColumn ? ColumnWidth.bigIcon : ColumnWidth.iconMin;
        return iconColumnWidth;
    }

    function _isGroupableColumn(fieldName: string, fieldType: ColumnFieldType): boolean {
        switch (fieldType) {
            case ColumnFieldType.Attachments:
            case ColumnFieldType.Hyperlink:
            case ColumnFieldType.Image:
            case ColumnFieldType.Note:
            case ColumnFieldType.FileSize:
            case ColumnFieldType.FileIcon:
                return false;
            default:
                // filename columns are not groupable
                let mappedType = getMappedColumnType(fieldName);
                return mappedType !== MappedColumnType.name && mappedType !== MappedColumnType.title;
        }
    }

    function _getDefaultColumn(key: string, name: string): ISPListColumn {
        return {
            key: key,
            name: name,
            minWidth: ColumnWidth.regularMin,
            width: ColumnWidth.regular,
            isVisible: true,
            isVisibleMobile: true,
            isCollapsable: true,
            sortable: false,
            isSorted: false,
            isIcon: false,
            isAscending: true,
            fieldType: ColumnFieldType.Text,
            filterable: false,
            groupable: false,
            resizable: false,
            isGrouped: false,
            isFiltered: false
        };
    }

    function _getCustomColumn(): ISPListColumn {
        return {
            key: 'custom',
            name: '',
            minWidth: ColumnWidth.regularMin,
            width: ColumnWidth.custom,
            isVisible: true,
            isVisibleMobile: true,
            isCollapsable: true,
            sortable: false,
            isSorted: false,
            isIcon: false,
            isHtml: false,
            isAscending: true,
            fieldType: ColumnFieldType.Text,
            filterable: false,
            groupable: false,
            customFields: []
        };
    }
}
